'use client';

import { Button } from '@heroui/button';
import { Link } from '@heroui/link';
import { Spinner } from '@heroui/spinner';
import { useEffect, useState } from 'react';
import { useLoadingCallback } from '@/lib/hooks/use-loading-callback';

type CommonProps = {
    onResend: () => Promise<unknown>;
    resendInterval?: number;
    variant?: 'button' | 'link';
};

type ButtonVariantProps = {
    variant?: 'button';
    children?: React.ReactNode;
};

type LinkVariantProps = {
    variant: 'link';
    children?: React.ReactNode;
};

type ResendControlProps = CommonProps & (ButtonVariantProps | LinkVariantProps);

export function ResendControl({
    onResend,
    resendInterval = 30,
    variant = 'button',
    children,
}: ResendControlProps) {
    const [countdown, setCountdown] = useState(0);
    const isRateLimited = countdown > 0;

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const [handleResend, isResending] = useLoadingCallback(async () => {
        const response = await onResend();

        // This assumes the passed onResend function handles its own toast notifications
        // for success and failure, as it has more context.

        // @ts-expect-error we can improve this later
        if (!response?.error) {
            setCountdown(resendInterval);
        }
    });

    const buttonText = isRateLimited
        ? `Resend in ${countdown}s`
        : children || 'Resend code';

    if (variant === 'link') {
        return (
            <Link
                className="mt-2 text-sm hover:cursor-pointer"
                color="primary"
                isDisabled={isResending || isRateLimited}
                onPress={handleResend}
            >
                {isResending ? <Spinner className="mr-2" size="sm" /> : null}
                {buttonText}
            </Link>
        );
    }

    return (
        <Button
            className="flex-1"
            isDisabled={isResending || isRateLimited}
            isLoading={isResending}
            onPress={handleResend}
            radius="sm"
            variant="faded"
        >
            {buttonText}
        </Button>
    );
}
